import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Moon } from "lucide-react";
import { COMPANY, NAV_LINKS } from "../mock/mockData";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#05050A]/85 backdrop-blur-2xl border-b border-white/5"
          : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="container-lux flex items-center justify-between h-20 md:h-24">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <div className="relative">
            <img
              src={COMPANY.logo}
              alt="Moon and Back Support"
              className="h-12 w-12 md:h-14 md:w-14 rounded-full object-cover moon-glow transition-transform duration-500 group-hover:scale-110"
            />
          </div>
          <div className="hidden sm:block leading-tight">
            <div className="font-serif text-lg md:text-xl tracking-wide text-white">
              Moon <span className="text-gold">and</span> Back
            </div>
            <div className="text-[10px] md:text-xs tracking-[0.3em] text-white/60 uppercase">
              Support
            </div>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-10" data-testid="desktop-nav">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              end={link.path === "/"}
              className={({ isActive }) =>
                `link-gold text-sm tracking-wide transition-colors ${
                  isActive ? "text-gold" : "text-white/80 hover:text-white"
                }`
              }
              data-testid={`nav-${link.label.toLowerCase()}`}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        {/* CTA + Portal */}
        <div className="hidden lg:flex items-center gap-4">
          <Link
            to="/portal"
            className="text-sm text-white/70 hover:text-gold transition-colors link-gold"
            data-testid="header-portal-link"
          >
            Client Portal
          </Link>
          <Link
            to="/book"
            className="btn-gold inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-xs font-semibold tracking-[0.15em] uppercase text-deep transition-all hover:bg-gold-light hover:scale-[1.03]"
            data-testid="header-book-btn"
          >
            <Moon className="w-4 h-4" />
            Book Now
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden text-white p-2"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-[#05050A]/98 backdrop-blur-2xl border-t border-white/5 fade-up" data-testid="mobile-menu">
          <div className="container-lux py-6 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                end={link.path === "/"}
                className={({ isActive }) =>
                  `py-3 px-2 text-base border-b border-white/5 ${
                    isActive ? "text-gold" : "text-white/80"
                  }`
                }
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/portal"
              className="py-3 px-2 text-base text-white/80 border-b border-white/5"
              data-testid="mobile-portal-link"
            >
              Client Portal
            </Link>
            <Link
              to="/book"
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 text-xs font-semibold tracking-[0.15em] uppercase text-deep"
              data-testid="mobile-book-btn"
            >
              <Moon className="w-4 h-4" />
              Book Now
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
